import { useState } from 'react';
import { adminControls, adminControlsSave, modelRouting, modelRoutingSave, orgLimitsSave, orgUsage, type ApiError } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, Waiting, fig, notServed, pill, pre, sx, useApi, useGuarded, usePage , LoadError } from './kit';
import { logoBg } from './logos';

const PROVIDER: Record<string, [string, string]> = {
  anthropic: ['Anthropic', 'anthropic.svg'], google: ['Google', 'google.png'], mistral: ['Mistral', 'mistral.png'], perplexity: ['Perplexity', 'perplexity.webp'],
  voyage: ['Voyage AI', 'voyage.png'], infermedica: ['Infermedica', 'infermedica.png'], mediktor: ['Mediktor', 'mediktor.png'],
};
const COST: Record<string, string> = { Haiku: '$', Sonnet: '$$', Opus: '$$$' };
const TIERS = ['Haiku', 'Sonnet', 'Opus'] as const;
const PLANES: [string, 'ingest' | 'quota', string[], Record<string, string>][] = [
  ['Ingestion · API spend', 'ingest', ['OFF', 'MONITOR', 'ENFORCE'],
    { OFF: 'Meter usage only, nothing is blocked.', MONITOR: 'Usage is metered and a breach is logged, but no request is refused.', ENFORCE: 'A provider over its cap is refused. The gate falls back to the next enabled provider.' }],
  ['User quota', 'quota', ['OFF', 'SOFT', 'HARD'],
    { OFF: 'Count user usage only, no limit applied.', SOFT: 'A user over quota is warned and throttled, never blocked.', HARD: 'A user over quota is refused until the period rolls.' }],
];
const k = (n: number) => (n ? (n / 1000).toFixed(1) + 'k' : '0');
const HEAD_NUM = (bg: string, c: string) => 'display:flex;align-items:center;justify-content:center;width:22px;height:22px;flex:0 0 22px;border-radius:6px;font-size:16px;font-weight:700;background:' + bg + ';color:' + c + ';';
const CAPT = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';

/** Models & Spend (design seIsSpend, T9317; sp* L21769-21774 and L22199-22288). */
export function SpendSection() {
  const { client } = useSession();
  const { changed } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const [scope, setScope] = useState<'GLOBAL' | 'By organization'>('GLOBAL');
  const [orgId, setOrgId] = useState('');
  const [loadedOrg, setLoadedOrg] = useState<string | null>(null);
  const [caps, setCaps] = useState<Record<string, string>>({});
  const [capsSaved, setCapsSaved] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const byOrg = scope !== 'GLOBAL' && !!loadedOrg;
  const ctl = useApi((c, s) => adminControls(c, { query: byOrg ? { organizationId: loadedOrg! } : {}, signal: s }), [byOrg, loadedOrg]);
  const routing = useApi((c, s) => modelRouting(c, { signal: s }), []);

  if (!ctl.data && !ctl.error) return <Skeleton rows={6} label="Loading models and spend" />;
  if (!ctl.data) return <LoadError error={ctl.error!} onRetry={ctl.reload} />;

  const d = ctl.data;
  const rows = (byOrg ? d.org : d.global) ?? d.global;
  const capOf = (p: string, fallback: number | null) => (caps[p] !== undefined ? caps[p] : fallback ? String(fallback) : '');
  const num = (s: string) => parseInt(String(s).replace(/[^0-9]/g, ''), 10);
  const capped = rows.filter((r) => { const c = num(capOf(r.provider, r.budget?.monthlyTokens ?? null)); return !isNaN(c) && c > 0; }).length;
  const over = rows.filter((r) => { const c = num(capOf(r.provider, r.budget?.monthlyTokens ?? null)); return !isNaN(c) && c > 0 && r.usage.tokens > c; }).length;
  const dirty = Object.keys(caps).length > 0 && !capsSaved;
  const enforcing = d.enforcement === 'enforce';
  const chipText = scope === 'GLOBAL' ? 'GLOBAL' : orgId.trim() || 'BY ORG';
  const planeCur = { ingest: d.enforcement.toUpperCase(), quota: d.userEnforcement.toUpperCase() };

  const run = async (fn: () => Promise<void>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const saveCaps = () => run(async () => {
    const set: Record<string, { monthlyTokens: number | null }> = {};
    Object.entries(caps).forEach(([p, v]) => { const n = num(v); set[p] = { monthlyTokens: isNaN(n) || !n ? null : n }; });
    await adminControlsSave(client, { body: { budgets: { scope: byOrg ? loadedOrg! : 'GLOBAL', set } } });
    setCaps({});
    setCapsSaved(true);
    ctl.reload();
  });

  return (
    <div data-testid="settings-spend">
      <div style={sx('display:flex;align-items:flex-start;gap:10px;border-radius:11px;padding:11px 14px;margin-bottom:14px;' + (enforcing ? 'background:rgba(47,138,111,.09);border:1px solid rgba(47,138,111,.3);color:#12503C;' : 'background:#FCFAF4;border:1px solid rgba(34,39,46,.13);color:#3C454F;'))}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={sx('flex:0 0 15px;')} aria-hidden="true">
          <path d="M2.4 12S5.8 5.6 12 5.6 21.6 12 21.6 12 18.2 18.4 12 18.4 2.4 12 2.4 12Z" />
          <circle cx="12" cy="12" r="2.6" />
        </svg>
        <span style={sx('font-size:16px;line-height:1.55;')}>
          <b style={sx('font-weight:700;')}>{I(enforcing ? 'Enforcing -' : 'Monitoring only -')}</b>
          {' '}
          {I(enforcing
            ? 'caps below are live. A provider over its cap is refused, and the gate falls back to the next enabled provider.'
            : 'no spend is being capped yet. Set caps below, then switch Ingestion to Enforce to activate the breaker.')}
        </span>
      </div>
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr)) 150px;box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);margin-bottom:14px;overflow:hidden;')}>
        {([
          ['Tokens this period', k(rows.reduce((n, r) => n + r.usage.tokens, 0)), '#22272E'],
          ['API calls', String(rows.reduce((n, r) => n + r.usage.calls, 0)), '#22272E'],
          ['Providers capped', capped + '/' + rows.length, capped ? '#12503C' : '#22272E'],
          ['Over cap', String(over), over ? '#B3261E' : '#12503C'],
        ] as [string, string, string][]).map((t) => (
          <div key={t[0]} style={sx('padding:12px 15px;box-sizing:border-box;border-right:1px solid rgba(34,39,46,.09);')}>
            <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;margin-bottom:6px;')}>{I(t[0])}</div>
            <div style={sx(fig(t[2], 26))}>{I(t[1])}</div>
          </div>
        ))}
        <div style={sx('padding:12px 15px;display:flex;flex-direction:column;align-items:flex-end;justify-content:center;gap:7px;')}>
          <span style={sx('font-size:14px;font-weight:700;letter-spacing:.07em;color:#264C7E;background:rgba(79,138,214,.16);border-radius:6px;padding:3px 9px;')}>{I(chipText)}</span>
          <span style={sx('font-size:16px;color:#54606C;')}>{'PERIOD ' + d.period}</span>
        </div>
      </div>
      <ErrLine error={err} style="margin:-4px 0 10px;" />
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,420px),1fr));gap:0 22px;align-items:start;')}>
        <div style={sx('min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx(HEAD_NUM('rgba(34,56,79,.14)', '#22384F'))}>1</span>
            <span style={sx('font-size:16px;font-weight:700;')}>Model routing</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{I('Which model answers which job · routing decides the bill')}</span>
          </div>
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;margin-bottom:16px;')}>
            {notServed(routing.error) && <Waiting what="Model routing" style="margin:0 0 10px;" />}
            {routing.error && !notServed(routing.error) && <ErrLine error={routing.error} style="margin:0 0 10px;" />}
            {(routing.data?.jobs ?? []).map((m) => (
              <div key={m.key} style={sx('margin-bottom:9px;')}>
                <div style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:5px;margin-bottom:7px;')}>
                  <span style={sx('font-size:16px;font-weight:600;')}>{I(m.name)}</span>
                  <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:#54606C;white-space:nowrap;')}>{I(COST[m.tier])}</span>
                </div>
                <div role="radiogroup" aria-label={m.name} style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border:1px solid rgba(34,39,46,.12);border-radius:8px;overflow:hidden;')}>
                  {TIERS.map((t2, i) => (
                    <a
                      key={t2}
                      href="#"
                      role="radio"
                      aria-checked={m.tier === t2}
                      {...wr(() => void run(async () => { await modelRoutingSave(client, { body: { job: m.key, tier: t2 } }); routing.reload(); }))}
                      style={sx('display:flex;align-items:center;justify-content:center;height:26px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (i > 0 ? 'border-left:1px solid rgba(34,39,46,.12);' : '') + (m.tier === t2 ? 'background:rgba(79,138,214,.14);color:#2F5C97;' : 'background:#FCFAF4;color:#54606C;'))}
                    >
                      {I(t2)}
                    </a>
                  ))}
                </div>
                <div style={sx('font-size:16px;color:#54606C;margin-top:6px;line-height:1.5;text-wrap:pretty;')}>{I(m.note)}</div>
              </div>
            ))}
            <div style={sx('font-size:16px;color:#54606C;padding-top:10px;border-top:1px solid rgba(34,39,46,.08);line-height:1.5;text-wrap:pretty;')}>
              {I('Routing is per job, not per organization - a facility never picks a model. Guardian screening deliberately runs on the cheapest tier because it runs on everything, and escalates only on a hit.')}
            </div>
          </div>
        </div>
        <div style={sx('min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx(HEAD_NUM('rgba(34,56,79,.14)', '#22384F'))}>2</span>
            <span style={sx('font-size:16px;font-weight:700;')}>Enforcement</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>How the two spend planes behave</span>
          </div>
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;margin-bottom:16px;')}>
            {PLANES.map(([name, key, modes, notes]) => {
              const cur = planeCur[key];
              return (
                <div key={key} style={sx('margin-bottom:14px;')}>
                  <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:8px;')}>
                    <span style={sx(CAPT)}>{I(name)}</span>
                    <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + (cur === 'OFF' ? '#54606C' : cur === 'ENFORCE' || cur === 'HARD' ? '#1F6B52' : '#5B6B7C') + ';')} />
                    <span style={sx('font-size:16px;font-weight:700;color:#54606C;')}>{I(cur)}</span>
                  </div>
                  <div role="radiogroup" aria-label={name} style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));border:1px solid rgba(34,39,46,.14);border-radius:9px;overflow:hidden;')}>
                    {modes.map((m, i) => (
                      <a
                        key={m}
                        href="#"
                        role="radio"
                        aria-checked={cur === m}
                        {...wr(() => void run(async () => {
                          await adminControlsSave(client, { body: key === 'ingest' ? { providerEnforcement: m.toLowerCase() as 'off' } : { userEnforcement: m.toLowerCase() as 'off' } });
                          ctl.reload();
                        }))}
                        style={sx('display:flex;align-items:center;justify-content:center;height:38px;font-size:16px;font-weight:600;text-decoration:none;' + (i > 0 ? 'border-left:1px solid rgba(34,39,46,.12);' : '') + (cur === m ? 'background:rgba(34,39,46,.07);color:#22272E;' : 'background:#FCFAF4;color:#54606C;'))}
                      >
                        {I(m)}
                      </a>
                    ))}
                  </div>
                  <div style={sx('font-size:16px;color:#54606C;margin-top:8px;')}>{I(notes[cur])}</div>
                </div>
              );
            })}
          </div>
          <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx(HEAD_NUM('rgba(68,80,94,.14)', '#44505E'))}>3</span>
            <span style={sx('font-size:16px;font-weight:700;')}>Scope</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{I(scope === 'GLOBAL' ? 'GLOBAL company ceiling' : 'per-organization ceiling')}</span>
          </div>
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;margin-bottom:16px;')}>
            <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
              {(['GLOBAL', 'By organization'] as const).map((n) => (
                <a key={n} href="#" aria-pressed={scope === n} onClick={(e) => { pre(e); setScope(n); setCaps({}); }} style={sx(pill(scope === n))}>
                  {I(n)}
                </a>
              ))}
            </div>
            <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;')}>
              <input type="text" aria-label="Organization ID" value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="Organization ID (blank = GLOBAL)" style={sx('width:100%;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;flex:1;')} />
              <a
                href="#"
                onClick={(e) => { pre(e); const v = orgId.trim(); setLoadedOrg(v || null); if (v) setScope('By organization'); else setScope('GLOBAL'); setCaps({}); }}
                style={sx('display:inline-flex;align-items:center;justify-content:center;height:36px;padding:0 18px;border-radius:8px;background:#22384F;color:#fff;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;')}
              >
                {I(loadedOrg && loadedOrg === orgId.trim() ? 'Loaded' : 'Load')}
              </a>
            </div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:9px;')}>
              {I(scope === 'GLOBAL' ? 'Blank means the company ceiling. Every organization draws against the same pool.' : 'Caps apply to this organization only. Leave the field blank to fall back to the global ceiling.')}
            </div>
          </div>
        </div>
      </div>
      <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
        <span style={sx(HEAD_NUM('rgba(47,138,111,.18)', '#12503C'))}>4</span>
        <span style={sx('font-size:16px;font-weight:700;')}>Provider spend</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{I('Current-month usage vs monthly cap · ' + (scope === 'GLOBAL' ? 'GLOBAL' : 'per-org'))}</span>
      </div>
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;')}>
        <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:13px;')}>
          <span style={sx('font-size:16px;font-weight:700;')}>
            {'Provider spend · '}
            {I(chipText)}
          </span>
          <span style={sx('flex:1;')} />
          <a
            href="#"
            role="button"
            aria-disabled={!dirty || !canWrite || undefined}
            title={roReason ?? undefined}
            onClick={(e) => { pre(e); if (dirty && canWrite) void saveCaps(); }}
            style={sx('display:inline-flex;align-items:center;justify-content:center;height:26px;padding:0 16px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (dirty ? 'background:#22384F;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);'))}
          >
            {I(capsSaved && !dirty ? 'Caps saved' : 'Save caps')}
          </a>
        </div>
        <div style={sx('display:grid;grid-template-columns:minmax(0,1.1fr) 1.3fr 150px 70px;gap:12px;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.12);')}>
          <span style={sx(CAPT)}>Provider</span>
          <span style={sx(CAPT)}>Usage</span>
          <span style={sx(CAPT + 'text-align:right;')}>Cap</span>
          <span style={sx(CAPT + 'text-align:right;')}>Enabled</span>
        </div>
        {rows.map((r) => {
          const [name, file] = PROVIDER[r.provider] ?? [r.provider, ''];
          const cap = capOf(r.provider, r.budget?.monthlyTokens ?? null);
          const capNum = num(cap);
          const isOver = !isNaN(capNum) && capNum > 0 && r.usage.tokens > capNum;
          const off = r.budget ? r.budget.enabled === false : false;
          return (
            <div key={r.provider} style={sx('display:grid;grid-template-columns:minmax(0,1.1fr) 1.3fr 150px 70px;gap:12px;align-items:center;padding:10px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
              <span style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
                <span style={sx(file
                  ? "background-image:" + logoBg(file) + ";background-size:contain;background-repeat:no-repeat;background-position:center;width:24px;height:24px;flex:0 0 24px;display:block;"
                  : 'display:flex;align-items:center;justify-content:center;width:24px;height:24px;flex:0 0 24px;border-radius:6px;font-size:16px;font-weight:700;color:#264C7E;background:rgba(79,138,214,.14);')}
                >
                  {I(file ? '' : name.slice(0, 2).toUpperCase())}
                </span>
                <span style={sx('min-width:0;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(name)}</span>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>{I(r.usage.calls + ' calls')}</span>
                </span>
              </span>
              <span style={sx('min-width:0;')}>
                <span style={sx('display:block;font-size:16px;color:#22272E;')}>{I(k(r.usage.tokens) + ' · ' + (isNaN(capNum) || !capNum ? 'uncapped' : isOver ? 'over cap' : 'within cap'))}</span>
                <span style={sx('display:block;height:4px;border-radius:3px;background:rgba(34,39,46,.08);margin-top:6px;')}>
                  <span style={sx('display:block;height:100%;border-radius:3px;width:' + (isNaN(capNum) || !capNum ? Math.min(100, r.usage.tokens / 600) : Math.min(100, (r.usage.tokens / capNum) * 100)) + '%;background:' + (isOver ? '#B3261E' : '#1F6B52') + ';')} />
                </span>
              </span>
              <input
                type="text"
                aria-label={name + ' monthly cap'}
                value={cap}
                readOnly={!canWrite}
                title={roReason ?? undefined}
                onChange={(e) => { setCaps((p) => ({ ...p, [r.provider]: e.target.value })); setCapsSaved(false); }}
                placeholder={'∞'}
                style={sx('width:100%;height:36px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;height:32px;text-align:right;')}
              />
              <a
                href="#"
                role="switch"
                aria-checked={!off}
                aria-label={name + ' enabled'}
                {...wr(() => void run(async () => {
                  await adminControlsSave(client, { body: { budgets: { scope: byOrg ? loadedOrg! : 'GLOBAL', set: { [r.provider]: { enabled: off } } } } });
                  ctl.reload();
                }))}
                style={sx('display:inline-flex;align-items:center;width:38px;height:22px;flex:0 0 38px;border-radius:13px;padding:2px;box-sizing:border-box;text-decoration:none;margin-left:auto;white-space:nowrap;' + (off ? 'background:rgba(34,39,46,.22);justify-content:flex-start;' : 'background:#1F6B52;justify-content:flex-end;'))}
              >
                <span style={sx('display:block;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(34,39,46,.28);')} />
              </a>
            </div>
          );
        })}
        <div style={sx('font-size:16px;color:#54606C;margin-top:11px;line-height:1.55;')}>
          {I('Current-month token usage against the monthly cap per upstream provider. Leave a cap blank for unlimited. A cap only bites once Ingestion is set to Enforce.')}
        </div>
      </div>
      {byOrg && <ChatLimits orgId={loadedOrg!} />}
    </div>
  );
}

const CATS: [string, string][] = [
  ['general', 'General chat'],
  ['financial', 'Money questions'],
  ['medical', 'Medical'],
  ['crisis', 'Crisis'],
];
/** Duty of care: medical and crisis are metered, never capped (usage-metering.ts). */
const NEVER_CAPPED = new Set(['medical', 'crisis']);

/**
 * Restored from the Flutter console (organizations_view.dart:641-802; no design control):
 * one organization's chat limits by category, monthly and per minute, read from GET /usage
 * and saved with PATCH /organizations/:id. Shown once an organization is loaded in Scope.
 */
export function ChatLimits({ orgId }: { orgId: string }) {
  const { client } = useSession();
  const { changed } = usePage();
  const { canWrite, roReason } = useGuarded();
  const usage = useApi((c, s) => orgUsage(c, { query: { organizationId: orgId }, signal: s }), [orgId]);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const lim = (cat: string) => usage.data?.llmLimits.find((l) => l.category === cat);
  const shown = (cat: string, k: 'monthly' | 'perMin') => {
    const key = cat + '.' + k;
    if (edits[key] !== undefined) return edits[key];
    const l = lim(cat);
    const v = l ? (k === 'monthly' ? l.monthlyLimit : l.perMinLimit) : null;
    return v === null || v === undefined || v < 0 ? '' : String(v);
  };
  const dirty = Object.keys(edits).length > 0;
  // Blank or not a number means unlimited (-1), as in the Flutter editor.
  const val = (s: string) => {
    const n = parseInt(s.replace(/[^0-9]/g, ''), 10);
    return isNaN(n) ? -1 : n;
  };
  const save = async () => {
    if (!dirty || !canWrite) return;
    setErr(null);
    try {
      const limits: Partial<Record<'general' | 'financial', { monthly: number; perMin: number }>> = {};
      for (const cat of ['general', 'financial'] as const) {
        if (edits[cat + '.monthly'] !== undefined || edits[cat + '.perMin'] !== undefined) limits[cat] = { monthly: val(shown(cat, 'monthly')), perMin: val(shown(cat, 'perMin')) };
      }
      await orgLimitsSave(client, { params: { id: orgId }, body: { limits } });
      setEdits({});
      setSaved(true);
      usage.reload();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  const inputS = 'width:100%;height:32px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;text-align:right;';
  return (
    <div data-testid="settings-chat-limits" style={sx('margin-top:16px;')}>
      <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
        <span style={sx(HEAD_NUM('rgba(47,138,111,.18)', '#12503C'))}>5</span>
        <span style={sx('font-size:16px;font-weight:700;')}>Chat limits</span>
        <span style={sx('flex:1;')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{I('Per category · monthly and per minute · ' + orgId)}</span>
      </div>
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:14px 16px;')}>
        <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:10px;')}>
          <span style={sx('font-size:16px;color:#54606C;')}>
            {I(usage.data ? (usage.data.enforcement === 'hard' ? 'Enforced: a building over a limit is refused until the period rolls.' : 'Recorded, not enforced yet (usage enforcement is ' + usage.data.enforcement + ').') : '')}
          </span>
          <span style={sx('flex:1;')} />
          <a
            href="#"
            role="button"
            aria-disabled={!dirty || !canWrite || undefined}
            title={roReason ?? undefined}
            onClick={(e) => { pre(e); void save(); }}
            style={sx('display:inline-flex;align-items:center;justify-content:center;height:26px;padding:0 16px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' + (dirty ? 'background:#22384F;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);'))}
          >
            {I(saved && !dirty ? 'Limits saved' : 'Save limits')}
          </a>
        </div>
        {usage.error && <ErrLine error={usage.error} style="margin:0 0 10px;" />}
        <ErrLine error={err} style="margin:0 0 10px;" />
        <div style={sx('display:grid;grid-template-columns:minmax(0,1.2fr) 150px 150px;gap:12px;padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.12);')}>
          <span style={sx(CAPT)}>Category</span>
          <span style={sx(CAPT + 'text-align:right;')}>Monthly</span>
          <span style={sx(CAPT + 'text-align:right;')}>Per minute</span>
        </div>
        {CATS.map(([cat, name]) => (
          <div key={cat} style={sx('display:grid;grid-template-columns:minmax(0,1.2fr) 150px 150px;gap:12px;align-items:center;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(name)}</span>
            {NEVER_CAPPED.has(cat) ? (
              <span style={sx('grid-column:2 / 4;text-align:right;font-size:16px;color:#1F6B52;font-weight:700;')}>{I('Always allowed · duty of care')}</span>
            ) : (
              (['monthly', 'perMin'] as const).map((k) => (
                <input
                  key={k}
                  type="text"
                  aria-label={name + (k === 'monthly' ? ' monthly limit' : ' per-minute limit')}
                  value={shown(cat, k)}
                  placeholder={'∞'}
                  readOnly={!canWrite}
                  title={roReason ?? undefined}
                  onChange={(e) => { setEdits((p) => ({ ...p, [cat + '.' + k]: e.target.value })); setSaved(false); }}
                  style={sx(inputS)}
                />
              ))
            )}
          </div>
        ))}
        <div style={sx('font-size:16px;color:#54606C;margin-top:11px;line-height:1.55;')}>
          {I('Leave a limit blank for unlimited. Medical and crisis questions are counted but never refused.')}
        </div>
      </div>
    </div>
  );
}
